/**
 * Storage monitoring: fridges, cool rooms and the shop floor.
 *
 * A reading outside the range opens an excursion on its own — that is a fact.
 * What it means for the stock inside is a person's decision, recorded with
 * their name: no impact, quarantine named batches, or dispose of them.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Thermometer } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, Modal, Select, Spinner, Textarea, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type BatchRow, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td } from './parts';
import { Checkbox, StockTabs } from './MedicinesPage';

interface Area {
  id: string;
  branch_id: string;
  branch_name: string;
  name: string;
  kind: string;
  min_celsius: string;
  max_celsius: string;
  last_reading: { reading: string; recorded_at: string; in_range: boolean } | null;
  open_excursion: boolean;
}

interface Excursion {
  id: string;
  branch_id: string;
  branch_name: string;
  area_name: string;
  started_at: string;
  ended_at: string | null;
  lowest_celsius: string;
  highest_celsius: string;
  min_celsius: string;
  max_celsius: string;
  status: string;
  decision: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
}

const when = (iso: string | null) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');

export function StoragePage(): ReactNode {
  const { can } = useAuth();
  const { branchId } = useWorkingLocation();
  const areas = usePharmacy<ListOf<Area>>('/storage-areas', { branchId });
  const excursions = usePharmacy<ListOf<Excursion>>('/excursions');
  const [logFor, setLogFor] = useState<Area | null>(null);
  const [historyFor, setHistoryFor] = useState<Area | null>(null);
  const [decide, setDecide] = useState<Excursion | null>(null);

  return (
    <div>
      <PageHeader title="Medicines & Stock" description="Temperatures, acceptable ranges and excursions, with who decided what" />
      <StockTabs />
      <div className="space-y-5">
        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader
              title="Storage areas"
              action={can('pharmacy:settings') ? <Link to="/pharmacy/settings" className="text-sm font-medium text-brand-700 hover:underline">Set up areas in Settings</Link> : undefined}
            />
          </div>
          {areas.isError ? (
            <div className="p-4"><ErrorState message="Could not load storage areas." onRetry={() => void areas.refetch()} /></div>
          ) : !areas.data ? (
            <Spinner />
          ) : areas.data.data.length === 0 ? (
            <EmptyState icon={<Thermometer className="size-8" />} title="No storage areas" description="Add fridges and rooms with their acceptable ranges in Pharmacy settings." />
          ) : (
            <div className="grid gap-3 p-4 pt-0 sm:grid-cols-2 sm:p-5 sm:pt-0 lg:grid-cols-3">
              {areas.data.data.map((a) => (
                <div key={a.id} className={cn('rounded-xl border p-4', a.open_excursion ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white')}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-500">{a.branch_name} · {a.kind} · {a.min_celsius}–{a.max_celsius} °C</p>
                    </div>
                    {a.open_excursion && <Badge tone="danger">excursion</Badge>}
                  </div>
                  <p className={cn('mt-3 text-2xl font-semibold tabular-nums', a.last_reading && !a.last_reading.in_range ? 'text-red-700' : 'text-slate-900')}>
                    {a.last_reading ? `${a.last_reading.reading} °C` : '—'}
                  </p>
                  <p className="text-xs text-slate-500">{a.last_reading ? `last read ${when(a.last_reading.recorded_at)}` : 'no reading yet'}</p>
                  <div className="mt-3 flex gap-2">
                    {can('storage:log') && <Button size="sm" onClick={() => setLogFor(a)}>Log reading</Button>}
                    <Button size="sm" variant="ghost" onClick={() => setHistoryFor(a)}>History</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padded={false}>
          <div className="p-4 sm:p-5"><CardHeader title="Excursions" subtitle="An excursion stays open until somebody who may decide records what it means for the stock" /></div>
          {!excursions.data ? (
            <Spinner />
          ) : excursions.data.data.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-500">No excursions recorded.</p>
          ) : (
            <Table head={['Area', 'Started', 'Ended', 'Range', 'Seen', 'Status', 'Decision', '']} minWidth={900}>
              {excursions.data.data.map((e) => (
                <tr key={e.id}>
                  <Td>{e.area_name}<p className="text-xs text-slate-500">{e.branch_name}</p></Td>
                  <Td className="tabular-nums">{when(e.started_at)}</Td>
                  <Td className="tabular-nums">{e.ended_at ? when(e.ended_at) : 'ongoing'}</Td>
                  <Td className="tabular-nums">{e.min_celsius}–{e.max_celsius} °C</Td>
                  <Td className="tabular-nums text-red-700">{e.lowest_celsius} to {e.highest_celsius} °C</Td>
                  <Td><StatusBadge status={e.status} /></Td>
                  <Td>{e.decision ? <>{e.decision.replace('_', ' ')}<p className="text-xs text-slate-500">{e.decided_by} · {e.decision_note}</p></> : '—'}</Td>
                  <Td className="text-right">{e.status === 'open' && can('storage:decide') && <Button size="sm" variant="secondary" onClick={() => setDecide(e)}>Decide</Button>}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
      {logFor && <LogReadingModal area={logFor} onClose={() => setLogFor(null)} />}
      {historyFor && <HistoryModal area={historyFor} onClose={() => setHistoryFor(null)} />}
      {decide && <DecideModal excursion={decide} onClose={() => setDecide(null)} />}
    </div>
  );
}

function LogReadingModal({ area, onClose }: { area: Area; onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>, { inRange: boolean }>('post', '/temperature-logs');
  const [reading, setReading] = useState('');
  const [note, setNote] = useState('');
  const ok = /^-?\d+(\.\d{1,2})?$/.test(reading.trim());
  return (
    <Modal open onClose={onClose} title={`Log a reading — ${area.name}`}>
      <div className="space-y-3">
        <Input label="Temperature (°C)" inputMode="decimal" value={reading} hint={`Acceptable: ${area.min_celsius}–${area.max_celsius} °C`} onChange={(e) => setReading(e.target.value)} />
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        {save.data && (
          <p className={cn('rounded-lg px-3 py-2 text-sm', save.data.inRange ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800')}>
            {save.data.inRange ? 'Recorded — in range.' : 'Recorded — OUT OF RANGE. An excursion is open; a pharmacist must decide what it means for the stock.'}
          </p>
        )}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>{save.data ? 'Close' : 'Cancel'}</Button>
          {!save.data && (
            <Button disabled={!ok} loading={save.isPending} onClick={() => save.mutate({ storageAreaId: area.id, readingCelsius: Number(reading), note: note.trim() || null })}>
              Record
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function HistoryModal({ area, onClose }: { area: Area; onClose: () => void }): ReactNode {
  const log = usePharmacy<ListOf<{ id: string; reading_celsius: string; in_range: boolean; recorded_at: string; recorded_by: string | null; note: string | null }>>(`/storage-areas/${area.id}/log`);
  return (
    <Modal open onClose={onClose} title={`Readings — ${area.name}`} size="lg">
      {!log.data ? (
        <Spinner />
      ) : log.data.data.length === 0 ? (
        <p className="text-sm text-slate-500">No readings yet.</p>
      ) : (
        <Table head={['When', 'Reading', 'By', 'Note']} minWidth={480}>
          {log.data.data.map((r) => (
            <tr key={r.id}>
              <Td className="tabular-nums">{when(r.recorded_at)}</Td>
              <Td className={cn('tabular-nums', !r.in_range && 'font-semibold text-red-700')}>{r.reading_celsius} °C</Td>
              <Td>{r.recorded_by ?? '—'}</Td>
              <Td>{r.note}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Modal>
  );
}

function DecideModal({ excursion, onClose }: { excursion: Excursion; onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>>('post', `/excursions/${excursion.id}/decide`);
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { branchId: excursion.branch_id });
  const [decision, setDecision] = useState('no_impact');
  const [note, setNote] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const needsBatches = decision !== 'no_impact';
  return (
    <Modal open onClose={onClose} title={`Decide: ${excursion.area_name}`} size="lg">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Seen {excursion.lowest_celsius} to {excursion.highest_celsius} °C against {excursion.min_celsius}–{excursion.max_celsius} °C, from {when(excursion.started_at)}.
        </p>
        <Select
          label="Decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          options={[
            { value: 'no_impact', label: 'No impact on the stock' },
            { value: 'quarantine', label: 'Quarantine the batches named below' },
            { value: 'dispose', label: 'Dispose — quarantine now, then decide disposal on each hold' },
          ]}
        />
        {needsBatches && (
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {!batches.data ? (
              <Spinner />
            ) : (
              batches.data.data.map((b) => (
                <Checkbox
                  key={b.batchId}
                  label={`${b.productName ?? 'Item'} · ${b.batchNumber} · ${b.onHand} on hand`}
                  checked={chosen.includes(b.batchId)}
                  onChange={(on) => setChosen((c) => (on ? [...c, b.batchId] : c.filter((x) => x !== b.batchId)))}
                />
              ))
            )}
          </div>
        )}
        <Textarea label="Reasoning" required value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={note.trim().length < 3 || (needsBatches && chosen.length === 0)}
            loading={save.isPending}
            onClick={() => save.mutate({ decision, note: note.trim(), batchIds: needsBatches ? chosen : [] }, { onSuccess: onClose })}
          >
            Record decision
          </Button>
        </div>
      </div>
    </Modal>
  );
}
